'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';

import { HORIZON_YEAR } from '../engine';

// One series per card, and it is always the bill: drawing utility against solar
// here would make the solar argument before the reader has typed anything.
const BILL_COLOR = 'uiChartGas';
const FELL_COLOR = 'uiSuccess';

const Illustration = ({ children }: { children: React.ReactNode }) => (
  <x.svg aria-hidden h="56px" maxW="100%" viewBox="0 0 140 60" w="160px">
    {children}
  </x.svg>
);

const STROKE_WIDTH = 2;

// What the results draw: actual history as bars, then the forecast as a line
// out of the last one
const HistoryAndForecastArt = () => (
  <Illustration>
    {[18, 20, 25, 28, 32].map((height, i) => (
      <x.rect fill={BILL_COLOR} height={height} key={height} rx={2} width={9} x={8 + i * 13} y={50 - height} />
    ))}
    <x.path
      d="M75 20 C 92 17, 108 12, 130 4"
      fill="none"
      stroke={BILL_COLOR}
      strokeLinecap="round"
      strokeWidth={STROKE_WIDTH}
    />
  </Illustration>
);

// The change-dot texture the history bars carry — size is how much the rate
// moved, green is a year rates fell, the ring is an announced filing. Showing
// it here pre-teaches the chart's legend.
const RateChangeArt = () => (
  <Illustration>
    {[
      { dot: 5, fell: false, height: 20, ring: false },
      { dot: 9, fell: false, height: 26, ring: false },
      { dot: 4, fell: true, height: 24, ring: false },
      { dot: 7, fell: false, height: 31, ring: false },
      { dot: 8, fell: false, height: 37, ring: true },
    ].map(({ dot, fell, height, ring }, i) => (
      <x.g key={height}>
        {ring ? (
          <x.circle cx={12 + i * 13} cy={50 - height - 9} fill="uiBg" r={dot / 2} stroke={BILL_COLOR} strokeWidth={2} />
        ) : (
          <x.circle cx={12 + i * 13} cy={50 - height - 9} fill={fell ? FELL_COLOR : BILL_COLOR} r={dot / 2} />
        )}
        <x.rect fill={BILL_COLOR} height={height} rx={2} width={9} x={8 + i * 13} y={50 - height} />
      </x.g>
    ))}
  </Illustration>
);

// The area under the curve — literally the number tier 1 reports
const RunningTotalArt = () => (
  <Illustration>
    <x.path d="M10 50 C 48 47, 92 40, 130 12 L 130 50 Z" fill={BILL_COLOR} fillOpacity={0.18} />
    <x.path
      d="M10 50 C 48 47, 92 40, 130 12"
      fill="none"
      stroke={BILL_COLOR}
      strokeLinecap="round"
      strokeWidth={STROKE_WIDTH}
    />
  </Illustration>
);

const CARDS = [
  {
    art: <HistoryAndForecastArt />,
    body: 'Check out your state’s actual rate history and what it means for your future utility bills.',
    title: 'Your 20 years of bills',
  },
  {
    art: <RateChangeArt />,
    body: 'Announced rate cases from your state’s regulator, the U.S. government’s forecast, and what drove the increases you already paid.',
    title: 'Why it goes up',
  },
  {
    art: <RunningTotalArt />,
    body: `The running total you’d hand the utility between now and ${HORIZON_YEAR}.`,
    title: 'What it adds up to',
  },
];

export const ProjectionPreview = () => (
  <DS.Flex gap={6} w="100%">
    <DS.Flex alignItems="center" flexDirection="row" gap={4}>
      <x.span bg="uiDivider" flex={1} h="1px" />
      <DS.Text color="uiHelperCopy" letterSpacing="0.08em" text="body12">
        WHAT THE PROJECTION SHOWS
      </DS.Text>
      <x.span bg="uiDivider" flex={1} h="1px" />
    </DS.Flex>

    <DS.Flex flexDirection={{ _: 'column', md: 'row' }} gap={4} w="100%">
      {CARDS.map((card) => (
        <DS.Flex
          borderColor="uiDivider"
          borderRadius={16}
          borderStyle="solid"
          borderWidth="1px"
          flex={1}
          gap={3}
          key={card.title}
          p={6}
          textAlign="left"
        >
          {card.art}
          <x.h2 fontWeight="medium" text={{ _: 'body16', md: 'body20' }}>
            {card.title}
          </x.h2>
          <DS.Text color="uiHelperCopy" text="body14">
            {card.body}
          </DS.Text>
        </DS.Flex>
      ))}
    </DS.Flex>

    <DS.Text as="p" color="uiHelperCopy" text="body14" textAlign="center">
      See how solar reduces costs, calculated on state averages.
    </DS.Text>
  </DS.Flex>
);
